import { PaymentModel} from "../../data/entities";
import { ODataGrid, TitlelessCard, ResponsiveGridColumn, Breakpoints, Button, IconAndText, IconDefinitions } from "../../components";
import { sharedProperty, useAsync, useCRUDHandlers, useGridActions, useSetPageTitle, useSharedState } from "../../hooks";
import { PaymentService } from "../../services/payment";
import { formatPrice, getRouteParams, showSuccessMessage } from "../../utils";
import { LocationService } from "../../services";
import { PaymentDetailsModal, PaymentSettingsModal,} from "./components";
import { useEffect, useState, } from "react";
import { CreatedDateCell, CustomerNameCell, PaymentStatusCell, BookingCancelledCell } from "./components/LocationPaymentGridCells";
import { CapturePreAuthRequestDTO, VoidPreAuthRequestDTO, RefundRequestDTO } from "../../data/entities/payment";
import { GridRowClickEvent } from "@progress/kendo-react-grid";
import { usePageActions } from "../PageLayout";
import { PaymentGatewayConfiguration } from "../../models";
import { MenuItem } from "@progress/kendo-react-layout";
import { routes } from "../../app/routes";

export function LocationPaymentsPage() {
  const params = getRouteParams('/locations/:locationId/payments', window.location.pathname);
  const locationId = params.get('locationId');
  const { value: location} = useAsync(() => {return new LocationService().get(parseInt(locationId ?? ""))}, [locationId]);
  const { value: paymentOptions } = useAsync( () => locationId ? new LocationService().getPaymentOptionsAsync(parseInt(locationId)) : Promise.resolve(null), [locationId]);
  const gridData = useSharedState<Array<PaymentModel>>([]);
  const paymentService = new PaymentService();
  const { gridRef, getItems } = useCRUDHandlers<PaymentModel>(paymentService, "Payment");
  const showSettingsState = useSharedState<boolean>(false);

  useSetPageTitle(`${location?.name}: Payments`, []);
  
  usePageActions(
    <Button onClick={() => showSettingsState.setter(true)}>
      <IconAndText iconName={IconDefinitions.gear} text="Configurations"/>
    </Button>
  );

  useEffect(() => {
    if (paymentOptions) {
        const config = paymentOptions as PaymentGatewayConfiguration;
        if (typeof config.configuration === "string") {
          try {
            config.configuration = JSON.parse(config.configuration);
          } catch {
            config.configuration = {};
          }
        }
        setInitalPaymentConfig(config);
      }
  }, [paymentOptions]);

  const [initalPaymentConfig, setInitalPaymentConfig] = useState<PaymentGatewayConfiguration | null>(null);

  const handleCapture = async (captureRequest: CapturePreAuthRequestDTO) => {
    const captureResponse = await paymentService.capturePreAuthAsync(captureRequest);
    if (captureResponse && gridRef.current) {
      showSuccessMessage("Pre-Auth Successfully Captured");
      gridRef.current.refreshData();
    }
  };

  const handleVoid = async (voidRequest: VoidPreAuthRequestDTO) => {
    const voidResponse = await paymentService.voidPreAuthAsync(voidRequest);
    if (voidResponse && gridRef.current) {
      showSuccessMessage("Pre-Auth Successfully Voided");
      gridRef.current.refreshData();
    }
  };

  const handleRefund = async (refundRequest: RefundRequestDTO) => {
    const refundResponse = await paymentService.refundChargeAsync(refundRequest);
    if (refundResponse && gridRef.current) {
      showSuccessMessage("Payment Successfully Refunded");
      gridRef.current.refreshData();
    }
  };

  const [selectedPayment, setSelectedPayment] = useState<sharedProperty<PaymentModel>>({
    value: {} as PaymentModel,
    setter: () => {}
  });
  
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const openPaymentModal = (payment: PaymentModel) => {
    setSelectedPayment({
      value: payment,
      setter: (val) => setSelectedPayment(prev => ({
        value: typeof val === 'function' ? val(prev.value) : val,
        setter: prev.setter
      }))
    });
    setShowPaymentModal(true);
  };

  const handleRowClick = (e: GridRowClickEvent) => {
    openPaymentModal(e.dataItem);
  };

  const { onSelect } = useGridActions<PaymentModel>(routes.location.payments);

  const handleRowMenuSelect = (event:any, dataItem:any) => {
    const action = event.item?.data?.action;
    if (action === "payments") {
      openPaymentModal(dataItem);
      return;
    }
    onSelect(event, dataItem);
  };

  return (
    <TitlelessCard bodyClassName="p-0">
      <ODataGrid
        ref={gridRef}
        getData={(oDataQuery) => {
          return getItems(oDataQuery + `&$expand=Invoice($expand=Bookings,Location,Customer,Payments)&$filter=Invoice/Location/Id eq ${locationId} and Invoice/InvoiceType eq 'Booking'`)
        }}
        dataState={gridData}
        scrollable="scrollable"
        pageSize={10}
        sort={[{ field: "createdDate", dir: "desc" }]} 
        onRowClick={handleRowClick} 
        onRowMenuSelect={handleRowMenuSelect}
      >
        <ResponsiveGridColumn
          title="Customer Name"
          field="invoice.customer"
          sortable={false}
          filterable={false}
          cell={CustomerNameCell}
        />
        <ResponsiveGridColumn
          title="Email"
          field="invoice.customer.email"
          filterable={false}
          sortable={true}
          breakpoint={Breakpoints.xl}
        />
        <ResponsiveGridColumn
          title="Date"
          field="createdDate"
          sortable={true}
          filterable={false}
          breakpoint={Breakpoints.md}
          cell={CreatedDateCell}
        />
        <ResponsiveGridColumn
          title="Amount"
          field="invoice.balance"
          sortable={true}
          filterable={false}
          cell={(props) => <td>{formatPrice(props.dataItem.total)}</td>}
        />
        <ResponsiveGridColumn
          title="Status"
          field="status"
          sortable={true}
          filterable={false}  
          cell={PaymentStatusCell}
        />
        <ResponsiveGridColumn
          title="Charge Type"
          field="chargeType"
          sortable={true}
          filterable={false}
          breakpoint={Breakpoints.lg}
        />
        <ResponsiveGridColumn
          title="Booking"
          field="invoice.bookings"
          sortable={false}
          filterable={false}
          breakpoint={Breakpoints.md}
          cell={BookingCancelledCell}
        />
        <MenuItem
          text="Details"
          data={{ action: "payments" }}
          icon="windows"
        />
      </ODataGrid>

      <PaymentDetailsModal
        showState={{ value: showPaymentModal, setter: setShowPaymentModal }}
        selectedPaymentState={selectedPayment}
        onCapture={handleCapture}
        onVoid={handleVoid}
        onRefund={handleRefund}
      />

      <PaymentSettingsModal 
        initialConfig={initalPaymentConfig ?? undefined}
        onSubmit={async (values: PaymentGatewayConfiguration) => {
          await new LocationService().addPaymentOptionsAsync(parseInt(locationId ?? ""), {
            ...values,
            configuration: JSON.stringify(values.configuration),
          });
        setInitalPaymentConfig(values); 
        showSuccessMessage("Payment options saved successfully.");
        }}
        showState={showSettingsState}    
      />
    </TitlelessCard>
  );
}

export default LocationPaymentsPage;